// src/pages/Projects.tsx
import React, { useEffect, useState } from 'react';
import './Projects.css';
import { FaReact, FaNodeJs, FaCode, FaServer, FaDatabase, FaPlay, FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { SiTypescript, SiMongodb, SiDjango, SiTensorflow, SiPython, SiTailwindcss, SiCplusplus } from 'react-icons/si';
import { Project } from '../types';
import { getProjects } from '../queries/getProjects';

// Tech icons mapping
const techIcons: { [key: string]: JSX.Element } = {
  "React": <FaReact />,
  "TypeScript": <SiTypescript />,
  "Node.js": <FaNodeJs />,
  "MongoDB": <SiMongodb />,
  "C": <SiCplusplus />,
  "Python": <SiPython />,
  "Django": <SiDjango />,
  "TensorFlow": <SiTensorflow />,
  "Tailwind": <SiTailwindcss />
};

// Project categories
const CATEGORIES = {
  'all': { icon: <FaPlay />, label: 'All Projects' },
  'fullstack': { icon: <FaCode />, label: 'Full Stack' },
  'system': { icon: <FaServer />, label: 'System Design' },
  'ml': { icon: <FaDatabase />, label: 'ML / Data Analysis' }
};

// Helper function to determine project categories
const getProjectCategories = (project: Project): string[] => {
  const title = project.title.toLowerCase();
  const tech = project.techUsed.toLowerCase();
  const categories = [];
  
  // System Design category
  if (title.includes('jbod') || title.includes('securewater')) {
    categories.push('system');
  }
  
  // ML/Data Analysis category
  if (title.includes('agriscience') || 
      title.includes('meal plan') || 
      title.includes('mymuscle') ||
      title.includes('stockfetcher') ||
      tech.includes('tensorflow') || 
      tech.includes('ml') || 
      tech.includes('machine learning') ||
      tech.includes('scikit-learn') ||
      tech.includes('pandas')) {
    categories.push('ml');
  }
  
  // Full Stack category - either explicitly assigned or fallback
  if (title.includes('m8') || 
      title.includes('meal plan') || 
      title.includes('homeharmony') ||
      title.includes('portfolio') ||
      title.includes('securewater') ||
      title.includes('course') ||
      title.includes('sar meditech') ||
      title.includes('finguard') ||
      !categories.length) {
    categories.push('fullstack');
  }
  
  return categories;
};

// Project links mapping
const PROJECT_LINKS: { [key: string]: { github?: string; live?: string } } = {
  "M8 Website": {
    github: "https://github.com/mantavya0807", // Generic profile link from Django template
    live: "https://www.m8-us.com/"
  },
  "HomeHarmony": {
    github: "https://github.com/mantavya0807", // No specific GitHub link found
    live: "https://sub-space.me/"
  },
  "M8 Social Mobile": {
    github: "https://github.com/mantavya0807" // Generic profile link
  },
  "AgriScience": {
    github: "https://github.com/mantavya0807/demo"
  },
  "Meal Plan Optimizer": {
    github: "https://github.com/mantavya0807/Mealer"
  },
  "SecureWater": {
    github: "https://github.com/mantavya0807/SecureWater"
  },
  "SAR Meditech Website": {
    github: "https://github.com/mantavya0807/SAR"
  },
  "Course Scheduler": {
    github: "https://github.com/mantavya0807/CourseCrafter"
  },
  "StockFetcher": {
    github: "https://github.com/mantavya0807/django" // Generic django link
  },
  // These projects weren't in the Django template - using profile link
  "FinGuard": {
    github: "https://github.com/mantavya0807"
  },
  "Portfolio": {
    github: "https://github.com/mantavya0807",
    live: window.location.origin // Current website
  },
  "MyMuscle": {
    github: "https://github.com/mantavya0807"
  },
  "JBOD System": {
    github: "https://github.com/mantavya0807"
  }
};

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewMode, setViewMode] = useState<'all' | 'fullstack' | 'system' | 'ml'>('all');
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { 
    async function fetchProjects() {
      setLoading(true);
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchProjects();
  }, []);
  
  if (loading) return <div className="loading">Loading...</div>;

  // Get featured projects (M8 and HomeHarmony)
  const featuredProjects = projects.filter(p => 
    p.title === "M8 Website" || p.title === "HomeHarmony"
  );
  
  // Other projects based on current filter
  const filteredProjects = viewMode === 'all' 
    ? projects.filter(p => !featuredProjects.some(f => f.title === p.title))
    : projects.filter(p => getProjectCategories(p).includes(viewMode) && 
                          !featuredProjects.some(f => f.title === p.title));

  // Toggle project expansion
  const toggleProject = (projectTitle: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (expandedProject === projectTitle) {
      setExpandedProject(null);
    } else {
      setExpandedProject(projectTitle);
    }
  };

  // Get category badge name
  const getCategoryBadgeName = (categories: string[]): string => {
    if (categories.includes('system')) return 'SYSTEM DESIGN';
    if (categories.includes('ml')) return 'ML / DATA';
    return 'FULL STACK';
  };

  // Get project links
  const getProjectLinks = (projectTitle: string) => {
    return PROJECT_LINKS[projectTitle] || { github: "https://github.com/mantavya0807" };
  };

  return (
    <div className="netflix-projects-container">
      {/* Banner section with red keyboard theme */}
      <div className="projects-banner">
        <div className="banner-content">
          <h1 className="netflix-title">INNOVATIVE PROJECTS</h1>
          
          <div className="netflix-meta">
            <span className="match">98% Match</span>
            <span className="year">2025</span>
            <span className="rating">DEV</span>
            <span className="duration">Full-time Ready</span>
          </div>

          <p className="banner-description">
            Building solutions with passion and creativity. From web apps to system design, explore my portfolio of technical projects.
            Recently, I've been focused on backend engineering—building reliable systems in Python and Scala.
          </p>

          <div className="banner-buttons">
            <button 
              className={`btn-play ${viewMode === 'all' ? 'active' : ''}`} 
              onClick={() => setViewMode('all')}
            >
              <FaPlay />
              <span>All Projects</span>
            </button>
            {Object.entries(CATEGORIES).slice(1).map(([key, { icon, label }]) => (
              <button 
                key={key}
                className={`btn-category ${viewMode === key ? 'active' : ''}`} 
                onClick={() => setViewMode(key as 'fullstack' | 'system' | 'ml')}
              >
                {icon}
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trending section with featured projects */}
      <section className="trending-section">
        <h2 className="netflix-category-title">Trending Innovations</h2>
        
        <div className="trending-projects-grid">
          {featuredProjects.map((project, index) => {
            const categories = getProjectCategories(project);
            const categoryBadge = getCategoryBadgeName(categories);
            const projectLinks = getProjectLinks(project.title);
            
            return (
              <div 
                key={index} 
                className={`trending-project-card ${index === 0 ? 'large' : 'medium'}`}
                onClick={(e) => toggleProject(project.title, e)}
              >
                <div className={`project-type-badge ${categories[0]}`}>
                  {categoryBadge}
                </div>
                
                <div className="project-card-content">
                  <h3 className="trending-title">{project.title}</h3>
                  <p className="trending-company">{project.title.includes("M8") ? "M8 Social Platform" : "Personal Project"}</p>
                  <p className="trending-date">{project.title.includes("M8") ? "August 2024 - May 2025" : "January 2025 - April 2025"}</p>
                  
                  <div className="trending-tech-stack">
                    {project.techUsed.split(', ').slice(0, 3).map((tech, idx) => {
                      const key = Object.keys(techIcons).find(k => tech.includes(k));
                      return (
                        <span key={idx} className="tech-tag">
                          {key && techIcons[key]} {tech}
                        </span>
                      );
                    })}
                    {project.techUsed.split(', ').length > 3 && (
                      <span className="tech-tag more">
                        +{project.techUsed.split(', ').length - 3} more
                      </span>
                    )}
                  </div>
                  
                  <div className="trending-description">
                    <p>{project.description}</p>
                  </div>
                  
                  {expandedProject === project.title && (
                    <div className="project-expanded-content">
                      <div className="project-links">
                        <a href={projectLinks.github} target="_blank" rel="noopener noreferrer" className="project-link">
                          <FaGithub /> GitHub
                        </a>
                        {projectLinks.live && (
                          <a href={projectLinks.live} target="_blank" rel="noopener noreferrer" className="project-link">
                            <FaExternalLinkAlt /> Live Demo
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Abstract grid of remaining projects */}
      <div className="netflix-category">
        <h2 className="netflix-category-title">
          {viewMode === 'all' ? 'The Workshop Collection' : CATEGORIES[viewMode].label}
        </h2>
      </div>

      <div className="projects-abstract-grid">
        {filteredProjects.map((project, index) => {
          const categories = getProjectCategories(project);
          const mainCategory = categories.includes('system') ? 'system' : 
                               categories.includes('ml') ? 'ml' : 'fullstack';
          const categoryBadge = getCategoryBadgeName(categories);
          const projectLinks = getProjectLinks(project.title);
          
          return (
            <div 
              key={index} 
              className={`project-abstract-card size-${index % 4 + 1}`}
              style={{
                animationDelay: `${index * 0.15}s`,
                backgroundColor: mainCategory === 'system' ? '#1e2a3a' : 
                                mainCategory === 'ml' ? '#2d2d39' : '#2a2e3d'
              }}
              onClick={(e) => toggleProject(project.title, e)}
            >
              <div className="card-glow"></div>
              <div className="card-content">
                <div className={`project-type-badge small ${mainCategory}`}>
                  {categoryBadge}
                </div>
                
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
                
                <div className="tech-tags-wrapper">
                  {project.techUsed.split(', ').slice(0, 3).map((tech, idx) => {
                    const key = Object.keys(techIcons).find(k => tech.includes(k));
                    return (
                      <span key={idx} className="tech-tag small">
                        {key && techIcons[key]} {tech}
                      </span>
                    );
                  })}
                  {project.techUsed.split(', ').length > 3 && (
                    <span className="tech-tag small more">
                      +{project.techUsed.split(', ').length - 3} more
                    </span>
                  )}
                </div>
                
                {expandedProject === project.title && (
                  <div className="expanded-project-content">
                    <div className="project-links">
                      <a href={projectLinks.github} target="_blank" rel="noopener noreferrer" className="project-link">
                        <FaGithub /> GitHub
                      </a>
                      {projectLinks.live && (
                        <a href={projectLinks.live} target="_blank" rel="noopener noreferrer" className="project-link">
                          <FaExternalLinkAlt /> Live Demo
                        </a>
                      )}
                    </div>
                    
                    <div className="full-tech-stack">
                      <h4>Full Tech Stack</h4>
                      <div className="tech-tags-full">
                        {project.techUsed.split(', ').map((tech, idx) => (
                          <span key={idx} className="tech-tag small">
                            {Object.keys(techIcons).find(k => tech.includes(k)) && 
                              techIcons[Object.keys(techIcons).find(k => tech.includes(k)) || '']} 
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Projects;